import { memo, MouseEvent } from 'react'
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import Button from '@hotmart/space-lib/dist/components/button'

import { Icon, Modal } from 'src/components'
import ModalBody from 'src/components/Modal/body'
import ModalFooter from 'src/components/Modal/footer'
import ModalHeader from 'src/components/Modal/header'
import i18n from 'src/i18n'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import { toastEventDispatch } from '@space-lib-events/toast'

import useBanMember from 'src/hook/useBanMember'
import { usePosts } from 'src/hook/usePosts'
import {
  Actions,
  Entities,
  EventNames,
  ResultsTypes,
  ResultType,
  ScreenNames
} from 'src/utils/datahub/enums'
import { useUser } from 'src/hook/useUser'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { useCommunityDetails } from '../context/communityDetailsContext'
import { BanMemberModalProps } from './types'

const BanMemberModal = ({
  isOpen,
  userId,
  collectionId,
  userName,
  closeModal,
  setRefreshPageReplies,
  refreshPageReplies
}: BanMemberModalProps) => {
  const { user } = useUser()
  const { setRefreshPage, setLoading } = usePosts()
  const { mutate: banMemberMutate, isLoading } = useBanMember()
  const { community } = useCommunityDetails()
  const { sendDataHubConsume } = useSendDataHubConsume()

  /* istanbul ignore next */
  const handleSendEvent = (result: ResultType, failureKind?: string) => {
    sendDataHubConsume({
      action: Actions.BLOCK,
      entity: Entities.BAN,
      event: {
        screenName: ScreenNames.BAN_MODAL,
        eventName: EventNames.TRY_TO_BLOCK_A_USER,
        user: {
          id: userId
        },

        creator: {
          id: user.id,
          name: user.name
        },

        community: {
          id: community.id,
          name: community.name
        },
        result,
        failureKind
      }
    })
  }

  const handleBanMember = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    banMemberMutate(
      { collectionId, userId },
      {
        onSuccess: /* istanbul ignore next */ () => {
          if (setRefreshPageReplies) {
            setRefreshPageReplies(!refreshPageReplies)
          } else {
            setLoading(true)
            setRefreshPage(true)
          }

          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: i18n.t('notifications.banned_member.success_v2', { user: userName }),
            position: ToastPosition.BOTTOM_RIGHT
          })

          handleSendEvent(ResultsTypes.SUCCESS)

          closeModal()
          setLoading(false)
        },
        onError: /* istanbul ignore next */ (error) => {
          toastEventDispatch({
            type: ToastType.DANGER,
            message: i18n.t('notifications.banned_member.error', { user: userName }),
            position: ToastPosition.BOTTOM_RIGHT
          })

          handleSendEvent(ResultsTypes.FAILURE, error.message)
        }
      }
    )
  }

  return (
    <Modal show={isOpen} onClose={closeModal}>
      <ModalHeader className="_px-5 _pt-5 _pb-3">
        <div className="_d-flex _align-items-center">
          <h2 className="_flex-1 _text-4 _m-0 _text-gray-700">
            {i18n.t('modal_ban_member.title')}
          </h2>

          <Button variation="default" className="_border-0 _ml-4" onClickCapture={closeModal}>
            <span className="_sr-only">{i18n.t('buttons.cancel')}</span>
            <Icon icon={faXmark} size="2x" />
          </Button>
        </div>
      </ModalHeader>

      <ModalBody className="_px-5 _pb-5 _pt-0 _text-break">
        <p className="_text-2 _text-gray-500">
          {i18n.t('modal_ban_member.description', { user: userName })}
        </p>
      </ModalBody>

      <ModalFooter className="_d-flex _justify-content-end _p-4">
        <Button variation="default" className="_border-0 _ml-4" onClickCapture={closeModal}>
          {i18n.t('buttons.cancel')}
        </Button>

        <Button
          loading={isLoading}
          disabled={isLoading}
          variation="danger"
          className="_border-0 _ml-4"
          onClickCapture={handleBanMember}
        >
          {i18n.t('buttons.ban_member')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default memo(BanMemberModal)
